import React, { useEffect, useState } from 'react';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Before we conclude this learning module, there are four final rules every ambassador must follow. First, represent Tribe accurately. If you do not know an answer, ask the Tribe team instead of guessing. Second, keep every referral legitimate. Fake accounts, self-referrals, duplicate accounts, purchased leads, or attempts to manipulate your numbers will result in immediate removal from the program. Third, do not have people who already know one another make up close to half or more of the same gathering. You can invite people from your personal network, but spread them across different gatherings. Tribe is designed to help strangers connect, and a large existing friend group can change the dynamic and make others feel excluded. Finally, be respectful during outreach. Follow up once, accept when someone is not interested, and move on.',
  },
];

const RULES = [
  {
    number: '01',
    title: 'Represent Tribe accurately.',
  },
  {
    number: '02',
    title: 'Keep every referral legitimate.',
  },
  {
    number: '03',
    title: 'Don’t stack an event.',
  },
  {
    number: '04',
    title: 'Be respectful during outreach.',
  },
];

const STAGE_TIMINGS = [
  2300,
  4300,
  6400,
  13100,
  23200,
  44800,
  50500,
];

const INTRO_CAPTIONS = [
  'Before we conclude',
  'there are four final rules',
  'every ambassador must follow',
];

export default function Rules({
  audioStatus,
  playbackId,
}) {
  const [visualStage, setVisualStage] = useState(0);

  useEffect(() => {
    if (audioStatus !== 'Playing') {
      return undefined;
    }

    setVisualStage(0);

    const timers = STAGE_TIMINGS.map(
      (time, index) =>
        window.setTimeout(
          () => setVisualStage(index + 1),
          time
        )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [audioStatus, playbackId]);

  useEffect(() => {
    if (audioStatus === 'Complete') {
      setVisualStage(7);
    }
  }, [audioStatus]);

  const showingRules = visualStage >= 3;
  const currentRuleIndex = Math.min(
    Math.max(visualStage - 3, 0),
    RULES.length - 1
  );
  const reviewing = visualStage >= 7;
  const caption = INTRO_CAPTIONS[
    Math.min(visualStage, INTRO_CAPTIONS.length - 1)
  ];

  return (
    <section className="rules-kinetic-slide">
      <style>{STYLES}</style>

      {!showingRules ? (
        <IntroCaption
          key={caption}
          text={caption}
        />
      ) : (
        <RuleStack
          currentIndex={currentRuleIndex}
          reviewing={reviewing}
        />
      )}
    </section>
  );
}

function IntroCaption({ text }) {
  return (
    <div className="rules-intro-caption">
      <WordText
        as="h1"
        text={text}
        wordDelay={220}
        handwrittenWords={
          text === 'there are four final rules'
            ? ['final', 'rules']
            : []
        }
      />
    </div>
  );
}

function RuleStack({ currentIndex, reviewing }) {
  const visibleRules = RULES.slice(0, currentIndex + 1);

  return (
    <div
      className={[
        'rule-stack-scene',
        reviewing ? 'is-reviewing' : '',
      ].join(' ')}
    >
      {visibleRules.map((rule, index) => {
        const current = index === currentIndex && !reviewing;

        return (
          <div
            className={[
              'rule-stack-item',
              current ? 'is-current' : 'is-previous',
            ].join(' ')}
            style={{
              '--previous-top': `${22 + index * 54}px`,
              '--review-top': `${72 + index * 92}px`,
            }}
            key={rule.number}
          >
            <span>{rule.number}</span>

            {current ? (
              <WordText
                as="h1"
                text={rule.title}
                wordDelay={150}
              />
            ) : (
              <h1>{rule.title}</h1>
            )}
          </div>
        );
      })}
    </div>
  );
}

function WordText({
  as: Element,
  text,
  wordDelay,
  handwrittenWords = [],
}) {
  const words = text.split(' ');

  return (
    <Element aria-label={text}>
      {words.map((word, index) => {
        const normalizedWord = word.replace(/[^A-Za-z]/g, '');

        return (
          <React.Fragment key={`${word}-${index}`}>
            <span
              className={[
                'rules-word',
                handwrittenWords.includes(normalizedWord)
                  ? 'is-handwritten'
                  : '',
              ].join(' ')}
              style={{
                '--rules-word-delay': `${index * wordDelay}ms`,
              }}
              aria-hidden="true"
            >
              {word}
            </span>

            {index < words.length - 1 ? ' ' : ''}
          </React.Fragment>
        );
      })}
    </Element>
  );
}

Rules.title = 'Final rules';
Rules.steps = STEPS;

const STYLES = `
  .rules-kinetic-slide {
    position: relative;
    min-height: 465px;
    width: 100%;
    overflow: hidden;
    color: #111;
    text-rendering: optimizeLegibility;
  }

  .rules-intro-caption {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .rules-intro-caption h1 {
    max-width: 820px;
    margin: 0;
    text-align: center;
    font-size: clamp(40px, 4.9vw, 64px);
    font-weight: 800;
    letter-spacing: -0.048em;
    line-height: 1;
  }

  .rules-word {
    display: inline-block;
    opacity: 0;
    transform: translateY(0.38em);
    animation: rules-word-in 340ms cubic-bezier(.18,.84,.22,1) forwards;
    animation-delay: var(--rules-word-delay);
  }

  .rules-word.is-handwritten {
    font-family: 'Ugly Handwriting', cursive;
    font-size: 1.16em;
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 0.82;
  }

  .rule-stack-scene {
    position: absolute;
    inset: 0;
  }

  .rule-stack-item {
    position: absolute;
    left: 50%;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: clamp(15px, 2vw, 22px);
    width: 96%;
    transition:
      top 850ms cubic-bezier(.18,.84,.22,1),
      transform 850ms cubic-bezier(.18,.84,.22,1),
      color 600ms ease,
      opacity 600ms ease;
  }

  .rule-stack-item > span {
    flex: 0 0 auto;
    font-weight: 720;
    letter-spacing: 0.06em;
    font-variant-numeric: tabular-nums;
    transition:
      font-size 700ms cubic-bezier(.18,.84,.22,1),
      color 600ms ease;
  }

  .rule-stack-item h1 {
    margin: 0;
    font-weight: 800;
    letter-spacing: -0.048em;
    line-height: 1;
    transition:
      font-size 700ms cubic-bezier(.18,.84,.22,1),
      color 600ms ease;
  }

  .rule-stack-item.is-current {
    top: 50%;
    color: #111;
    transform: translate(-50%, -50%);
  }

  .rule-stack-item.is-current > span {
    color: #777;
    font-size: 11px;
  }

  .rule-stack-item.is-current h1 {
    font-size: clamp(38px, 4.9vw, 64px);
  }

  .rule-stack-item.is-previous {
    top: var(--previous-top);
    justify-content: center;
    color: #a2a2a2;
    transform: translateX(-50%);
  }

  .rule-stack-item.is-previous > span {
    flex: 0 0 28px;
    text-align: right;
    color: #aaa;
    font-size: 9px;
  }

  .rule-stack-item.is-previous h1 {
    width: min(72vw, 620px);
    text-align: left;
    font-size: 17px;
    font-weight: 620;
    letter-spacing: -0.025em;
    line-height: 1.15;
  }

  .rule-stack-scene.is-reviewing .rule-stack-item {
    top: var(--review-top);
    justify-content: center;
    color: #111;
    transform: translateX(-50%);
  }

  .rule-stack-scene.is-reviewing .rule-stack-item > span {
    flex: 0 0 30px;
    text-align: right;
    color: #777;
    font-size: 10px;
  }

  .rule-stack-scene.is-reviewing .rule-stack-item h1 {
    width: min(72vw, 650px);
    text-align: left;
    font-size: clamp(22px, 2.7vw, 32px);
    font-weight: 740;
    letter-spacing: -0.038em;
    line-height: 1.05;
  }

  @keyframes rules-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 720px) {
    .rules-kinetic-slide {
      min-height: 520px;
    }

    .rule-stack-item {
      gap: 12px;
    }

    .rule-stack-item.is-current {
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    .rule-stack-item.is-current h1 {
      font-size: 40px;
    }
  }

  @media (max-width: 480px) {
    .rules-intro-caption h1 {
      font-size: 39px;
    }

    .rule-stack-item.is-previous h1 {
      font-size: 15px;
    }

    .rule-stack-scene.is-reviewing .rule-stack-item h1 {
      font-size: 20px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .rules-kinetic-slide,
    .rules-kinetic-slide * {
      animation-duration: 0.01ms !important;
      animation-delay: 0ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`;
